<template>
	<div class="position-relative login-bg-root">
		<!-- Animated background (ported from LandingStrasbourg) -->
		<div class="bg-gradient"></div>
		<div class="bg-orb bg-orb-1"></div>
		<div class="bg-orb bg-orb-2"></div>
		<div class="bg-orb bg-orb-3"></div>
		<div class="bg-grid"></div>
		<img class="bg-plane" src="@/assets/icon-plane.png" alt="" aria-hidden="true" />

		<div class="authentication-wrapper authentication-basic container-p-y p-4 p-sm-0">
			<div class="authentication-inner py-6">
				<!-- Register Card -->
				<div class="card p-md-7 p-1">
					<!-- Logo -->
					<div class="app-brand justify-content-center mt-5">
						<a href="javascript:void(0)" class="app-brand-link gap-2">
							<span class="app-brand-logo demo">
								<span style="color: var(--bs-primary)">
									<img :src="logoUrl" :style="logoSize()" alt="Logo">
								</span>
							</span>
						</a>
					</div>

					<div class="card-body mt-1" style="text-align: center;" v-if="submitted">
						<img src="@/assets/img/login-2fa.gif" style="width: 70px;">
						<p class="mt-5">
							{{ $t('auth.login.twoFactorMessage') }}
						</p>

						<p class="text-center">
							<a href="javascript:void(0)" @click="submitted = false">
								<span>{{ $t('common.actions.back') }}</span>
							</a>
						</p>
					</div>

					<!-- /Logo -->
					<div class="card-body mt-1" v-if="!submitted">
						<p class="mb-5 text-center">{{ $t('auth.login.welcome', { brand: brandName }) }}</p>

						<form id="formAuthentication" @submit.prevent="loginUser" class="mb-5">
							<div class="form-floating form-floating-outline mb-5">
								<input type="text" class="form-control" id="email" name="email" :placeholder="$t('auth.login.emailPlaceholder')" v-model="form.email" @input="clearError('email')" />
								<label for="email">{{ $t('common.fields.email') }}</label>
								<small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
							</div>
							<div class="form-floating form-floating-outline mb-5">
								<input type="password" class="form-control" id="password" name="password" :placeholder="$t('auth.login.passwordPlaceholder')" v-model="form.password" @input="clearError('password')" />
								<label for="password">{{ $t('common.fields.password') }}</label>
								<small v-if="errors.password" class="text-danger">{{ errors.password }}</small>
							</div>
							
							<button class="btn btn-primary d-grid w-100" type="submit">{{ $t('auth.login.submit') }}</button>
						</form>

						<p class="text-center" v-if="displayRegister">
							<span>{{ $t('auth.login.noAccount') }}</span>
							<router-link to="/register">
								<span>{{ $t('auth.login.register') }}</span>
							</router-link>
						</p>

						<p class="text-center">
							<span>{{ $t('auth.login.forgotPassword') }}</span>
							<router-link to="/forgot-password">
								<span>{{ $t('auth.login.forgotPasswordLink') }}</span>
							</router-link>
						</p>
					</div>
				</div>
				<!-- Register Card -->
			</div>
		</div>
	</div>
</template>

<script>
import "@/assets/vendor/css/pages/page-auth.css";

import HTTP from "@/axios";

export default {
	name: "LoginPage",

	data() {
		return {
			domain: null,
			loginToken: null,
			submitted: false,
			form: {
				email: "",
				password: ""
			},
			errors: {},
		};
	},

	async mounted() {
		this.domain = window.location.hostname;
		this.form.email = this.$route.query.email;

		// Page demandée avant la connexion, gardée pour le lien 2FA ouvert dans un autre onglet
		const redirect = this.$route.query.redirect;
		if (this.isSafeRedirect(redirect))
			localStorage.setItem("loginRedirect", redirect);
		else if (!this.$route.query.token)
			localStorage.removeItem("loginRedirect");

		// Login direct via JWT dans l'URL: /login?jwt=xxx
		const jwt = this.$route.query.jwt;
		if (jwt) {
			this.$store.dispatch("saveJwt", jwt);
			// Supprime le JWT de l'historique navigateur
			window.history.replaceState({}, '', '/login');
			this.$router.push("/");
			return;
		}

		this.loginToken = this.$route.query.token;

		if (this.loginToken) {
			await this.loginWithToken();
		}
	},

	computed: {
		displayRegister() {
			if (this.domain === "seris.pika-aero.com")
				return false;

			if (this.domain === "excellence-academy.pika-aero.com")
				return false;

			if (this.domain === "vinci.pika-aero.com")
				return false;

			if (this.domain === "aviapartner.pika-aero.com")
				return false;

			return true;
		},

		logoUrl() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return require('@/assets/logo-exl.png');
			}
			else if (this.domain === "seris.pika-aero.com") {
				return require('@/assets/logo-seris.svg');
			}
			else if (this.domain === "alltranspack.pika-aero.com") {
				return require('@/assets/logo-alltranspack.webp');
			}
			else if (this.domain === "ifma.pika-aero.com") {
				return require('@/assets/logo-ifma.png');
			}
			else if (this.domain === "vinci.pika-aero.com") {
				return require('@/assets/logo-vinci.png');
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return require('@/assets/logo-strasbourg.png');
			}
			else if (this.domain === "aviapartner.pika-aero.com") {
				return require('@/assets/logo-aviapartner.webp');
			} else {
				return require('@/assets/logo.png');
			}
		},

		brandName() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return "Excellence Academy";
			}
			else if (this.domain === "seris.pika-aero.com") {
				return "Seris Academy";
			}
			else if (this.domain === "alltranspack.pika-aero.com") {
				return "Alltranspack";
			}
			else if (this.domain === "ifma.pika-aero.com") {
				return "IFMA";
			}
			else if (this.domain === "vinci.pika-aero.com") {
				return "Vinci Airport";
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return "Aéroport de Strasbourg";
			}
			else if (this.domain === "aviapartner.pika-aero.com") {
				return "Aviapartner";
			} else {
				return "Pika Aero";
			}
		}
	},

	methods: {
		isSafeRedirect(path) {
			return typeof path === "string" && path.startsWith("/") && !path.startsWith("//");
		},

		redirectAfterLogin() {
			const redirect = localStorage.getItem("loginRedirect");
			localStorage.removeItem("loginRedirect");
			this.$router.push(this.isSafeRedirect(redirect) ? redirect : "/");
		},

		logoSize() {
			if (this.domain === "excellence-academy.pika-aero.com") {
				return { width: "120px" };
			}
			else if (this.domain === "seris.pika-aero.com") {
				return { width: "100px" };
			}
			else if (this.domain === "alltranspack.pika-aero.com") {
				return { width: "120px" };
			}
			else if (this.domain === "ifma.pika-aero.com") {
				return { width: "120px" };
			}
			else if (this.domain === "vinci.pika-aero.com") {
				return { width: "120px" };
			}
			else if (this.domain === "strasbourg.pika-aero.com") {
				return { width: "180px" };
			}
			else if (this.domain === "aviapartner.pika-aero.com") {
				return { width: "180px" };
			} else {
				return { width: "160px" };
			}
		},

		clearError(field) {
			// Supprime l'erreur pour le champ spécifié
			if (this.errors[field]) {
				delete this.errors[field];
			}
		},

		validateForm() {
			this.errors = {};

			if (this.form.email)
				this.form.email = (this.form.email).trim();

			if (!this.form.email || !this.validateEmail(this.form.email)) {
				this.errors.email = this.$t('validation.invalidEmail');
			}
			if (!this.form.password) {
				this.errors.password = this.$t('validation.invalidPassword');
			}
			
			return Object.keys(this.errors).length === 0;
		},

		validateEmail(email) {
			const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			return re.test(email);
		},

		async loginUser() {
			const validated = this.validateForm();

			if (!validated)
				return;

			try {
				const response = await HTTP.post('/auth/login', this.form);

				if (response.data["2FA"]) {
					this.submitted = true;
				}
				else {
					this.$store.dispatch("saveJwt", response.data.accessToken);
					this.redirectAfterLogin();
				}
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		},

		async loginWithToken() {
			try {
				const response = await HTTP.post('/auth/login-with-token', { token: this.loginToken });

				// Save JWT
				this.$store.dispatch("saveJwt", response.data.accessToken);

				// Setup company
				this.redirectAfterLogin();
			} catch (error) {
				// Récupérer le message d'erreur de l'API ou définir un message par défaut
				const errorMessage = error?.response?.data?.message || this.$t('errors.generic');

				alert(errorMessage);

				// Log détaillé dans la console (utile en développement)
				console.error("Erreur API:", error);
			}
		}
	}
};
</script>

<style scoped>
.login-bg-root {
	min-height: 100vh;
}

.login-bg-root .authentication-wrapper {
	position: relative;
	z-index: 3;
}

.login-bg-root .card {
	background-color: rgba(255, 255, 255, 0.85);
	backdrop-filter: blur(12px);
	-webkit-backdrop-filter: blur(12px);
	border: 1px solid rgba(255, 255, 255, 0.6);
	box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08);
}

/* Animated background */
.bg-gradient {
	position: fixed;
	inset: 0;
	background: linear-gradient(135deg, #eef2ff 0%, #faf5ff 40%, #fef3f2 100%);
	z-index: 0;
}

.bg-orb {
	position: fixed;
	border-radius: 50%;
	filter: blur(80px);
	opacity: 0.5;
	z-index: 1;
	pointer-events: none;
}

.bg-orb-1 {
	width: 500px;
	height: 500px;
	background: radial-gradient(circle, #818cf8 0%, transparent 70%);
	top: -150px;
	left: -100px;
	animation: float-1 18s ease-in-out infinite;
}

.bg-orb-2 {
	width: 600px;
	height: 600px;
	background: radial-gradient(circle, #c084fc 0%, transparent 70%);
	bottom: -200px;
	right: -150px;
	animation: float-2 22s ease-in-out infinite;
}

.bg-orb-3 {
	width: 400px;
	height: 400px;
	background: radial-gradient(circle, #38bdf8 0%, transparent 70%);
	top: 40%;
	left: 50%;
	transform: translateX(-50%);
	opacity: 0.3;
	animation: float-3 15s ease-in-out infinite;
}

@keyframes float-1 {
	0%, 100% { transform: translate(0, 0) scale(1); }
	50% { transform: translate(60px, 80px) scale(1.1); }
}

@keyframes float-2 {
	0%, 100% { transform: translate(0, 0) scale(1); }
	50% { transform: translate(-80px, -60px) scale(1.15); }
}

@keyframes float-3 {
	0%, 100% { transform: translate(-50%, 0) scale(1); }
	50% { transform: translate(-50%, -40px) scale(1.08); }
}

.bg-grid {
	position: fixed;
	inset: 0;
	background-image:
		linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px),
		linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px);
	background-size: 60px 60px;
	mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
	-webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
	z-index: 2;
	pointer-events: none;
}

.bg-plane {
	position: fixed;
	width: 168px;
	height: auto;
	top: 15%;
	right: 8%;
	z-index: 2;
	pointer-events: none;
	user-select: none;
	animation: plane-drift 12s ease-in-out infinite;
}

@keyframes plane-drift {
	0%, 100% { transform: translate(0, 0); }
	50% { transform: translate(20px, -15px); }
}
</style>
